import { HStack } from "@/components/layout/HStack";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command/Command";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";
import { Input } from "@/components/ui/Input/Input";
import { Label } from "@/components/ui/Label/Label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover/Popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectStyles,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import {
  SheetBody,
  SheetClose,
  SheetFooter,
} from "@/components/ui/Sheet/Sheet";
import { Textarea } from "@/components/ui/Textarea/Textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { GRADE_OPTIONS } from "@/constants/gradeOptions";
import { PATHWAY_OPTIONS } from "@/constants/pathwayOptions";
import { useAuth } from "@/context/authContext";
import { normalize } from "@/utils/normalize";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Check, ChevronDownIcon, OctagonX } from "lucide-react";
import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { useMyExplorations } from "../../hooks/myExplorations/useMyExplorations";

const schema = z
  .object({
    rotation: z
      .string("Rotation must be a string")
      .min(1, "A rotation is required"),
    multiSession: z.boolean().default(false),
    session: z.string().default(""),
    sessions: z.array(z.string()).default([]),
    name: z
      .string("Name must be a string")
      .min(1, "A name is required")
      .max(120, "Exploration name cannot exceed 120 characters"),
    description: z
      .string("Description must be a string")
      .min(1, "A description is required")
      .max(1024, "Exploration description cannot exceed 1024 characters"),
    gradeLevels: z
      .array(z.number("Grade levels must be a number"))
      .min(1, "At least one grade level is required"),
    pathway: z.enum(
      ["Arts", "STEM", "Wellness", "Humanities"],
      "Pathway must be a string",
    ),
    room: z.string("Room must be a string").min(1, "A room is required"),
    minEnrollment: z.coerce.number("Minimum enrollment must be a number"),
    maxEnrollment: z.coerce.number("Max enrollment must be a number"),
    coTeachers: z.array(z.string("Co teachers must be strings")).default([]),
  })
  .superRefine((data, ctx) => {
    if (data.multiSession) {
      if (!data.sessions || data.sessions.length < 2) {
        ctx.addIssue({
          code: "custom",
          path: ["sessions"],
          message: "Select at least two sessions for a multi-session exploration",
        });
      }
    } else if (!data.session) {
      ctx.addIssue({
        code: "custom",
        path: ["session"],
        message: "A session is required",
      });
    }
  });

export type CreateFormInput = z.input<typeof schema>;
export type CreateFormOutput = z.output<typeof schema>;

const CreateExplorationForm = ({
  data,
  onSuccess,
  setExplorations,
  prefill,
}: {
  data: {
    rotations: any[];
    sessions: any[];
    teachers: any[];
  };
  onSuccess: () => void;
  setExplorations: Dispatch<
    SetStateAction<{
      explorations: any[];
      classes: any[];
      rotations: any[];
      sessions: any[];
      teachers: any[];
    }>
  >;
  prefill?: Partial<CreateFormInput>;
}) => {
  const { state } = useAuth();
  const { createExploration } = useMyExplorations();

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm<CreateFormInput, unknown, CreateFormOutput>({
    resolver: zodResolver(schema),
    defaultValues: prefill,
  });

  const rotation = watch("rotation");
  const multiSession = watch("multiSession");
  const selectedSessions = watch("sessions") ?? [];

  const rotationSessions = useMemo(() => {
    if (!rotation) return [];
    return data.sessions.filter((s) => s.rotation === rotation);
  }, [rotation, data.sessions]);

  const firstPickedSingleton = useMemo(() => {
    if (!multiSession || !selectedSessions.length) return undefined;
    const first = data.sessions.find((s) => s._id === selectedSessions[0]);
    return first?.singleton as boolean | undefined;
  }, [multiSession, selectedSessions, data.sessions]);

  const onSubmit: SubmitHandler<CreateFormOutput> = async (formData) => {
    const payload = formData.multiSession
      ? {
          ...formData,
          sessions: formData.sessions,
          session: undefined,
        }
      : {
          ...formData,
          session: formData.session,
          sessions: undefined,
        };
    const created = await createExploration(
      payload as unknown as CreateFormOutput,
      onSuccess,
    );
    if (!created) return;
    const newExplorations = created.explorations ?? (created.exploration ? [created.exploration] : []);
    setExplorations((prev) => ({
      ...prev,
      explorations: [...prev.explorations, ...newExplorations],
      classes: [...prev.classes, ...(created.classes ?? [])],
    }));
  };

  return (
    <>
      <SheetBody>
        <form id="create-exploration-form" onSubmit={handleSubmit(onSubmit)}>
          <VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Rotation</Label>
                {errors.rotation && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{errors.rotation.message}</TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Controller
                control={control}
                name="rotation"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(v) => {
                      field.onChange(v);
                      setValue("session", "");
                    }}
                  >
                    <SelectTrigger aria-invalid={!!errors.rotation}>
                      <SelectValue placeholder="Select a rotation" />
                    </SelectTrigger>
                    <SelectContent>
                      {data.rotations.map((r) => (
                        <SelectItem key={r._id} value={r._id}>
                          {format(r.date, "MMMM dd, yyyy")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </VStack>
            {rotation && (
              <VStack gap="2px">
                <HStack gap="0.5rem" align="center">
                  <Controller
                    control={control}
                    name="multiSession"
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        id="multiSession"
                        checked={!!field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          setValue("session", "");
                          setValue("sessions", []);
                        }}
                      />
                    )}
                  />
                  <Label htmlFor="multiSession">Multi-session exploration</Label>
                </HStack>
                {multiSession && (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--muted-foreground)",
                    }}
                  >
                    All selected sessions must share the same singleton type and
                    rotation. Students who pick this in any session will be
                    enrolled in (or have it set as their first choice across)
                    all of them.
                  </span>
                )}
              </VStack>
            )}
            {rotation && !multiSession && (
              <VStack gap="2px">
                <HStack gap="0.25rem">
                  <Label>Session</Label>
                  {errors.session && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <OctagonX
                          style={{
                            color: "var(--danger)",
                            width: "1rem",
                            height: "1rem",
                            cursor: "default",
                          }}
                        />
                      </TooltipTrigger>
                      <TooltipContent>{errors.session.message}</TooltipContent>
                    </Tooltip>
                  )}
                </HStack>
                <Controller
                  control={control}
                  name="session"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(v) => field.onChange(v)}
                    >
                      <SelectTrigger aria-invalid={!!errors.session}>
                        <SelectValue placeholder="Select a session" />
                      </SelectTrigger>
                      <SelectContent>
                        {rotationSessions.map((s) => (
                          <SelectItem key={s._id} value={s._id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </VStack>
            )}
            {rotation && multiSession && (
              <VStack gap="2px">
                <HStack gap="0.25rem">
                  <Label>Sessions</Label>
                  {errors.sessions && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <OctagonX
                          style={{
                            color: "var(--danger)",
                            width: "1rem",
                            height: "1rem",
                            cursor: "default",
                          }}
                        />
                      </TooltipTrigger>
                      <TooltipContent>{errors.sessions.message}</TooltipContent>
                    </Tooltip>
                  )}
                </HStack>
                <Controller
                  control={control}
                  name="sessions"
                  render={({ field }) => {
                    const selected = field.value ?? [];
                    const selectedLabel =
                      selected.length === 0
                        ? "Select sessions"
                        : selected.length === 1
                          ? rotationSessions.find(
                              (s) => s._id === selected[0],
                            )?.name ?? "1 session selected"
                          : `${selected.length} sessions selected`;

                    const toggle = (id: string) => {
                      const next = selected.includes(id)
                        ? selected.filter((x) => x !== id)
                        : [...selected, id];
                      field.onChange(next);
                    };

                    return (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            aria-invalid={!!errors.sessions}
                            className={SelectStyles.trigger}
                            data-placeholder={selected.length ? undefined : ""}
                          >
                            {selectedLabel}
                            <ChevronDownIcon
                              className={SelectStyles.triggerIcon}
                            />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          style={{
                            width: "var(--radix-dropdown-menu-trigger-width)",
                            overflow: "hidden",
                          }}
                        >
                          {rotationSessions.map((s) => {
                            const disabled =
                              firstPickedSingleton !== undefined &&
                              s.singleton !== firstPickedSingleton &&
                              !selected.includes(s._id);
                            return (
                              <DropdownMenuCheckboxItem
                                key={s._id}
                                checked={selected.includes(s._id)}
                                disabled={disabled}
                                onCheckedChange={() => toggle(s._id)}
                                onSelect={(e) => e.preventDefault()}
                              >
                                {s.name}
                                {s.singleton ? " (singleton)" : ""}
                              </DropdownMenuCheckboxItem>
                            );
                          })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );
                  }}
                />
              </VStack>
            )}
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Name</Label>
                {errors.name && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{errors.name.message}</TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Input
                {...register("name")}
                aria-invalid={!!errors.name}
                placeholder="Exploration name"
              />
            </VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Description</Label>
                {errors.description && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      {errors.description.message}
                    </TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Textarea
                {...register("description")}
                aria-invalid={!!errors.description}
                placeholder="Exploration description"
              />
            </VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Grades</Label>
                {errors.gradeLevels && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      {errors.gradeLevels.message}
                    </TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Controller
                control={control}
                name="gradeLevels"
                render={({ field }) => {
                  const selected = field.value ?? [];

                  const selectedLabel = (() => {
                    if (selected.length === 0) return "Select grade levels";
                    if (selected.length === 1) {
                      const grade = GRADE_OPTIONS.find(
                        (o) => o === selected[0],
                      );
                      return grade ? `${grade}th grade` : "1 grade selected";
                    }
                    return `${selected.length} grades selected`;
                  })();

                  const toggle = (grade: number) => {
                    const next = selected.includes(grade)
                      ? selected.filter((g) => g !== grade)
                      : [...selected, grade].sort((a, b) => a - b);

                    field.onChange(next);
                  };

                  return (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          id="date"
                          aria-invalid={!!errors.gradeLevels}
                          className={SelectStyles.trigger}
                          data-placeholder={field.value ? undefined : ""}
                        >
                          {selectedLabel}
                          <ChevronDownIcon
                            className={SelectStyles.triggerIcon}
                          />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        style={{
                          width: "var(--radix-dropdown-menu-trigger-width)",
                          overflow: "hidden",
                        }}
                      >
                        {GRADE_OPTIONS.map((o) => (
                          <DropdownMenuCheckboxItem
                            key={o}
                            checked={selected.includes(o)}
                            onCheckedChange={() => toggle(o)}
                            onSelect={(e) => e.preventDefault()}
                          >
                            {o}th grade
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }}
              />
            </VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Pathway</Label>
                {errors.pathway && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{errors.pathway.message}</TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Controller
                control={control}
                name="pathway"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(v) => field.onChange(v)}
                  >
                    <SelectTrigger aria-invalid={!!errors.pathway}>
                      <SelectValue placeholder="Select a pathway" />
                    </SelectTrigger>
                    <SelectContent>
                      {PATHWAY_OPTIONS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Room</Label>
                {errors.room && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{errors.room.message}</TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Input
                {...register("room")}
                aria-invalid={!!errors.room}
                placeholder="Exploration room"
              />
            </VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Min Enrollment</Label>
                {errors.minEnrollment && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      {errors.minEnrollment.message}
                    </TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Input
                {...register("minEnrollment")}
                type="number"
                aria-invalid={!!errors.minEnrollment}
                placeholder="Minimum enrollment"
              />
            </VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Max Enrollment</Label>
                {errors.maxEnrollment && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      {errors.maxEnrollment.message}
                    </TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Input
                {...register("maxEnrollment")}
                type="number"
                aria-invalid={!!errors.maxEnrollment}
                placeholder="Maximum enrollment"
              />
            </VStack>
            <VStack gap="2px">
              <HStack gap="0.25rem">
                <Label>Co-Teachers</Label>
                {errors.coTeachers && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <OctagonX
                        style={{
                          color: "var(--danger)",
                          width: "1rem",
                          height: "1rem",
                          cursor: "default",
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{errors.coTeachers.message}</TooltipContent>
                  </Tooltip>
                )}
              </HStack>
              <Controller
                control={control}
                name="coTeachers"
                render={({ field }) => {
                  const [open, setOpen] = useState<boolean>(false);
                  const selected = field.value ?? [];

                  const TEACHER_OPTIONS = (data as any).teachers
                    .filter((t: any) => t._id !== state.user!._id)
                    .map((t: any) => ({
                      value: t._id as string,
                      label: t.name as string,
                    }));

                  const label = (() => {
                    if (selected.length === 0) return "Select co-teachers";
                    if (selected.length === 1) {
                      const one = TEACHER_OPTIONS.find(
                        (t: any) => t.value === selected[0],
                      );
                      return one?.label ?? "1 co-teacher selected";
                    }
                    return `${selected.length} co-teachers selected`;
                  })();

                  const toggle = (id: string) => {
                    const next = selected.includes(id)
                      ? selected.filter((x) => x !== id)
                      : [...selected, id];

                    field.onChange(next);
                  };

                  return (
                    <Popover open={open} onOpenChange={setOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          id="date"
                          aria-invalid={errors.coTeachers ? true : false}
                          className={SelectStyles.trigger}
                          {...((field.value?.length === 0 || !field.value) && {
                            "data-placeholder": "",
                          })}
                        >
                          {label}
                          <ChevronDownIcon
                            className={SelectStyles.triggerIcon}
                          />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        style={{
                          width: "var(--radix-popover-trigger-width)",
                          overflow: "hidden",
                          padding: 0,
                        }}
                        align="start"
                      >
                        <Command
                          filter={(itemValue, search) => {
                            const value = normalize(itemValue);
                            const query = normalize(search);
                            if (!query) return 1;
                            return value.includes(query) ? 1 : 0;
                          }}
                        >
                          <CommandInput placeholder="Search teachers..." />
                          <CommandList>
                            <CommandEmpty>No teachers found.</CommandEmpty>
                            <CommandGroup>
                              {TEACHER_OPTIONS.map((opt: any) => {
                                const isSelected = selected.includes(opt.value);

                                return (
                                  <CommandItem
                                    key={opt.value}
                                    value={opt.label}
                                    onSelect={() => {
                                      toggle(opt.value);
                                    }}
                                  >
                                    <HStack
                                      align="center"
                                      justify="space-between"
                                      style={{ width: "100%" }}
                                    >
                                      {opt.label}
                                      {isSelected && (
                                        <Check
                                          style={{
                                            width: "1rem",
                                            height: "1rem",
                                            color:
                                              "var(--secondary-foreground)",
                                          }}
                                        />
                                      )}
                                    </HStack>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  );
                }}
              />
            </VStack>
          </VStack>
        </form>
      </SheetBody>
      <SheetFooter>
        <Button
          type="submit"
          form="create-exploration-form"
          style={{ width: "100%" }}
        >
          Create Exploration
        </Button>
        <SheetClose asChild>
          <Button variant="outline" style={{ width: "100%" }}>
            Cancel
          </Button>
        </SheetClose>
      </SheetFooter>
    </>
  );
};

export { CreateExplorationForm };
